import React, { useMemo } from 'react';
import { ISigningAppointmentResponse } from '../../../../types/signingAppointment/ISigningAppointmentResponse';
import {
  BadgeColorsBySigningAppointmentStatusTypes,
  eSigningAppointmentStatusTypes,
} from '../../../../types/signingAppointment/ISigningAppointmentStatusResponse';
import { BadgeTW } from '../../../../tailwindComponents/badgeTW/BadgeTW';
import { SignatureSyncProgressionBadge } from './SignatureSyncProgressionBadge';
import { useListStatusesQuery } from '../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { eSigningAppointmentStatus } from '../../../../types/signingAppointment/eSigningAppointmentStatus';
import { ISigningAppointmentDashboardResponse } from '../../../../types/signingAppointment/ISigningAppointmentDashboardResponse';

interface IAppointmentStatusBadgeProps {
  appointment: ISigningAppointmentResponse | ISigningAppointmentDashboardResponse;
}

export const AppointmentStatusBadge: React.FC<IAppointmentStatusBadgeProps> = ({ appointment }) => {
  return (
    <>
      <DigitalSchedulingStatusBadge appointment={appointment} />
      <AppointmentBadge appointment={appointment} />
    </>
  );
};

const BadgeSelector: React.FC<{
  statusType: eSigningAppointmentStatusTypes | undefined;
  testId: string;
  shortLabel?: string;
}> = ({ statusType, testId, shortLabel }) => {
  if (!statusType) {
    return null;
  }

  const label = shortLabel ?? statusType.replace(/-/g, ' ');
  const colorClass = BadgeColorsBySigningAppointmentStatusTypes[statusType];

  return (
    <BadgeTW data-test-id={testId} className={`${colorClass} max-h-8 capitalize`}>
      {label}
    </BadgeTW>
  );
};

export const DigitalSchedulingStatusBadge: React.FC<IAppointmentStatusBadgeProps> = ({ appointment }) => {
  const { statusType, testId } = useMemo(() => {
    if (appointment.status === eSigningAppointmentStatusTypes.Cancelled) {
      return {
        statusType: eSigningAppointmentStatusTypes.Cancelled,
        testId: `${appointment.id}-overview-cancelled-badge`,
      };
    }
    if (appointment.isDigitalSchedulingDraft) {
      return { statusType: eSigningAppointmentStatusTypes.Draft, testId: `${appointment.id}-overview-draft-badge` };
    }
    if (appointment.isDigitalSchedulingConfirmed) {
      return {
        statusType: eSigningAppointmentStatusTypes.SignerConfirmed,
        testId: `${appointment.id}-overview-email-badge`,
      };
    }
    return {
      statusType: eSigningAppointmentStatusTypes.SelectionPending,
      testId: `${appointment.id}-overview-email-badge`,
    };
  }, [appointment]);

  if (!appointment.isDigitalScheduling) {
    return null;
  }

  return <BadgeSelector statusType={statusType} testId={testId} />;
};

const AppointmentBadge: React.FC<IAppointmentStatusBadgeProps> = ({ appointment }) => {
  if (appointment.isDigitalSchedulingDraft) {
    return null;
  }
  if (appointment.status === eSigningAppointmentStatus.Cancelled && appointment.isDigitalScheduling) {
    return null;
  }
  if (!appointment.isSignatureSyncAppointment) {
    return <NonSigSyncAppointmentBadge appointment={appointment} />;
  }
  return <SignatureSyncProgressionBadge appointmentId={appointment.id} />;
};

export const NonSigSyncAppointmentBadge: React.FC<IAppointmentStatusBadgeProps> = ({ appointment }) => {
  const { data: signingStatuses = [] } = useListStatusesQuery();
  const appointmentStatus = useMemo(
    () => signingStatuses.find((status) => status.id === appointment?.status),
    [appointment, signingStatuses]
  );
  if (!appointmentStatus) return null;
  return (
    <BadgeSelector
      statusType={appointmentStatus.type}
      testId={`${appointment.id}-overview-status-badge`}
      shortLabel={appointmentStatus.shortLabel}
    />
  );
};
